import React, { useState, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import styled from '@emotion/native';
import Switch from '@/shared/components/Switch';
import Button from '@/shared/components/Button';
import { BodyText } from '@/shared/components/StyledText';
import { UPDATE_NOTIFICATION_PREFERENCES } from '@/shared/mutations';
import { GET_USER_NOTIFICATION_SETTINGS } from '@/shared/queries';
import { green, lightGreen, white, sand } from '@/shared/styles/colors';
import useIsDesktop from '@/shared/hooks/useIsDesktop';
import {
  MutationUpdateNotificationPreferencesArgs,
  MutationResult,
  QueryResult,
  Maybe,
  User,
} from '@/shared/types/generated';
import {
  SectionContainer,
  SectionHeader,
} from '@/shared/styles/styledComponents/settings';
import useStore from '@/stores/useStore';

interface Props {
  user: Maybe<User>;
}

const NotificationsSection = ({ user }: Props) => {
  const isDesktop = useIsDesktop();
  const { setSnackbarState } = useStore();
  const [notificationPreferences, setNotificationPreferences] = useState({
    emailNotificationsEnabled: true,
    pushNotificationsEnabled: true,
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const { data: notificationData, loading: notificationLoading } = useQuery<
    QueryResult<'getUserNotificationSettings'>
  >(GET_USER_NOTIFICATION_SETTINGS, {
    onCompleted: (data) => {
      if (data?.getUserNotificationSettings) {
        setNotificationPreferences({
          emailNotificationsEnabled:
            data.getUserNotificationSettings.emailNotificationsEnabled,
          pushNotificationsEnabled:
            data.getUserNotificationSettings.pushNotificationsEnabled,
        });
      }
    },
    onError: (error) => {
      setSnackbarState({
        open: true,
        message: 'Failed to load notification settings',
        severity: 'error',
      });
    },
  });

  const [updateNotificationPreferences, { loading: isSavingPreferences }] =
    useMutation<
      MutationResult<'updateNotificationPreferences'>,
      MutationUpdateNotificationPreferencesArgs
    >(UPDATE_NOTIFICATION_PREFERENCES, {
      onCompleted: () => {
        setHasUnsavedChanges(false);
        setSnackbarState({
          open: true,
          message: 'Your notification preferences have been updated.',
          severity: 'success',
        });
      },
      onError: (error) => {
        setSnackbarState({
          open: true,
          message: 'Failed to update notification preferences',
          severity: 'error',
        });
      },
    });

  useEffect(() => {
    if (
      user &&
      !notificationLoading &&
      !notificationData?.getUserNotificationSettings
    ) {
      setNotificationPreferences({
        emailNotificationsEnabled: user.emailNotificationsEnabled ?? true,
        pushNotificationsEnabled: user.pushNotificationsEnabled ?? true,
      });
    }
  }, [user, notificationLoading, notificationData]);

  const handleNotificationToggle = (type: 'email' | 'push', value: boolean) => {
    const key =
      type === 'email'
        ? 'emailNotificationsEnabled'
        : 'pushNotificationsEnabled';
    const updatedPreferences = {
      ...notificationPreferences,
      [key]: value,
    };
    setNotificationPreferences(updatedPreferences);
    setHasUnsavedChanges(true);
  };

  const handleSaveNotificationPreferences = async () => {
    await updateNotificationPreferences({
      variables: {
        input: notificationPreferences,
      },
    });
  };

  return (
    <SectionContainer>
      {isDesktop ? (
        <DesktopHeaderContainer>
          <DesktopHeaderTextContainer>
            <SectionHeader>Notifications:</SectionHeader>
            <NotificationSubtext>
              Instant styling, chat and session-related updates
            </NotificationSubtext>
          </DesktopHeaderTextContainer>
          <DesktopSaveButtonContainer>
            <Button
              text={isSavingPreferences ? 'Saving...' : 'Save Changes'}
              onPress={handleSaveNotificationPreferences}
              disabled={!hasUnsavedChanges || isSavingPreferences}
              variant={hasUnsavedChanges ? 'primary' : 'secondary'}
              customStyle={{ paddingHorizontal: 50, height: 60 }}
            />
          </DesktopSaveButtonContainer>
        </DesktopHeaderContainer>
      ) : (
        <>
          <SectionHeader>Notifications:</SectionHeader>
          <NotificationSubtext>
            Instant styling, chat and session-related updates
          </NotificationSubtext>
        </>
      )}

      <NotificationContainer isDesktop={isDesktop}>
        <NotificationRow>
          <NotificationLabel>Email</NotificationLabel>
          <Switch
            value={notificationPreferences.emailNotificationsEnabled}
            onValueChange={(value) => handleNotificationToggle('email', value)}
            disabled={notificationLoading || isSavingPreferences}
          />
        </NotificationRow>

        <NotificationDivider />

        <NotificationRow>
          <NotificationLabel>Push notifications</NotificationLabel>
          <Switch
            value={notificationPreferences.pushNotificationsEnabled}
            onValueChange={(value) => handleNotificationToggle('push', value)}
            disabled={notificationLoading || isSavingPreferences}
          />
        </NotificationRow>
      </NotificationContainer>

      {!isDesktop && (
        <SaveButtonContainer>
          <Button
            text={isSavingPreferences ? 'Saving...' : 'Save Changes'}
            onPress={handleSaveNotificationPreferences}
            disabled={!hasUnsavedChanges || isSavingPreferences}
            variant={hasUnsavedChanges ? 'primary' : 'secondary'}
            customStyle={{ width: '100%' }}
          />
        </SaveButtonContainer>
      )}
    </SectionContainer>
  );
};

export default NotificationsSection;

const NotificationSubtext = styled(BodyText)({
  fontSize: 16,
  color: lightGreen,
  marginTop: 4,
  marginBottom: 16,
});

const NotificationContainer = styled.View<{ isDesktop: boolean }>(
  ({ isDesktop }) => ({
    backgroundColor: white,
    borderRadius: 10,
    paddingHorizontal: isDesktop ? 0 : 16,
    marginBottom: 16,
  }),
);

const NotificationRow = styled.View({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  paddingVertical: 16,
});

const NotificationLabel = styled(BodyText)({
  fontSize: 16,
  color: green,
  flex: 1,
  fontWeight: '400',
});

const NotificationDivider = styled.View({
  height: 1,
  backgroundColor: sand,
  marginHorizontal: 0,
});

const SaveButtonContainer = styled.View({
  marginTop: 8,
  marginBottom: 16,
  alignItems: 'center',
});

const DesktopHeaderContainer = styled.View({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: 16,
});

const DesktopHeaderTextContainer = styled.View({
  flex: 1,
  marginRight: 20,
});

const DesktopSaveButtonContainer = styled.View({
  alignItems: 'flex-end',
});
